import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./JoinCircleSheet.css";
import { CoinIcon } from "./RewardCard";
import { joinCircle } from "../cafemenuApi";

const TERMS_URL = "https://bluetokaicoffee.com/pages/blue-tokai-circle-terms-conditions";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isGuest = (name) => /^guest$/i.test(String(name || "").trim());

/**
 * Join Circle inside the menu. The server decides what is needed (it knows the account), this just follows:
 * it opens by asking to join; if the account already has a name and a verified email that is the whole job,
 * otherwise it asks for the missing name / email, then the code emailed to that address.
 * `fallbackUrl` is the join link — only its encrypted id is used (the web page's API); nothing opens outside the menu.
 */
export default function JoinCircleSheet({ fallbackUrl, account, onJoined, onClose }) {
  const [view, setView] = useState("working");   // working | details | otp | done | conflict | error
  const [busy, setBusy] = useState(true);
  const [note, setNote] = useState("");
  const [missing, setMissing] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [terms, setTerms] = useState(false);
  const [emailMasked, setEmailMasked] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [fieldError, setFieldError] = useState("");

  const sheetRef = useRef(null);
  const mounted = useRef(true);
  const started = useRef(false);
  const lastInput = useRef({});
  const onJoinedRef = useRef(onJoined);
  onJoinedRef.current = onJoined;

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const handle = useCallback((r, input) => {
    if (!mounted.current && r.outcome !== "joined" && r.outcome !== "already_member") return;
    if (r.outcome === "joined" || r.outcome === "already_member") {
      onJoinedRef.current?.(r.outcome);
      if (mounted.current) setView("done");
      return;
    }
    if (r.outcome === "need_details") {
      setMissing(r.missing?.length ? r.missing : ["name", "email"]);
      setName(isGuest(r.name) ? "" : (r.name || ""));
      setEmail(r.email || "");
      setNote(Object.keys(input).length ? r.message : "");
      setView("details");
    } else if (r.outcome === "need_email_otp") {
      setEmailMasked(r.emailMasked || "your email");
      setResendIn(Math.max(0, Number(r.resendAfterSeconds) || 0));
      setCode("");
      setNote(input.emailOtp ? r.message : "");
      setView("otp");
    } else if (r.outcome === "account_conflict") {
      setView("conflict");
    } else {
      setNote(r.message || "We couldn't complete this right now. Please try again.");
      setView("error");
    }
  }, []);

  const run = useCallback(async (input) => {
    lastInput.current = input;
    setBusy(true);
    try {
      const r = await joinCircle(input, { joinUrl: fallbackUrl, ...account });
      handle(r, input);
    } catch (e) {
      if (!mounted.current) return;
      setNote(e?.message || "We couldn't complete this right now. Please try again.");
      setView("error");
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [handle, fallbackUrl, account]);

  // Opening the sheet is the customer's "Join Circle": if the account is ready this finishes right here.
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    // Like the web page: show the details (prefilled) and the Terms first; nothing is sent until they agree.
    const accountName = String(account?.name || "").trim();
    setMissing(["name", "email"]);
    setName(accountName && !isGuest(accountName) ? accountName : "");
    setEmail(String(account?.email || ""));
    setBusy(false);
    setView("details");
  }, [account]);

  // resend countdown
  useEffect(() => {
    if (view !== "otp" || resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn((n) => Math.max(0, n - 1)), 1000);
    return () => clearTimeout(timer);
  }, [view, resendIn]);

  // the sheet rides above the on-screen keyboard
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const update = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      sheetRef.current?.style.setProperty("--kb", `${Math.round(inset)}px`);
    };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => { vv.removeEventListener("resize", update); vv.removeEventListener("scroll", update); };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // the code submits itself on the sixth digit
  useEffect(() => {
    if (view === "otp" && /^\d{6}$/.test(code) && !busy) {
      run({ name: name || undefined, email, emailOtp: code });
    }
  }, [code]);   // eslint-disable-line

  const submitDetails = (e) => {
    e.preventDefault();
    const n = name.trim().replace(/\s+/g, " ");
    const m = email.trim().toLowerCase();
    if (missing.includes("name") && (n.length < 2 || !/\p{L}/u.test(n) || isGuest(n))) { setFieldError("Enter your full name."); return; }
    if (missing.includes("email") && !EMAIL_RE.test(m)) { setFieldError("Enter a valid email address."); return; }
    if (!terms) { setFieldError("Please accept the Terms & Conditions to continue."); return; }
    setFieldError("");
    setName(n);
    setEmail(m);
    run({ name: n || undefined, email: m || undefined });
  };

  const body = () => {
    if (view === "working") {
      return (
        <div className="jc-center" role="status" aria-busy="true">
          <span className="jc-spinner" aria-hidden="true" />
          <p className="jc-title">Setting up your membership…</p>
        </div>
      );
    }

    if (view === "done") {
      return (
        <div className="jc-center">
          <span className="jc-badge" aria-hidden="true"><CoinIcon size={34} /></span>
          <p className="jc-title">Welcome to Circle</p>
          <p className="jc-sub">You're a member now. Your Circle Coins and rewards are ready.</p>
          <button type="button" className="jc-btn" onClick={onClose}>Continue</button>
        </div>
      );
    }

    if (view === "conflict") {
      return (
        <div className="jc-center">
          <p className="jc-title">We need a hand</p>
          <p className="jc-sub">We found multiple accounts linked to your details. Please contact Customer Support to complete your enrollment.</p>
          <button type="button" className="jc-btn" onClick={() => { setMissing(["email"]); setEmail(""); setNote(""); setView("details"); }}>Use a different email</button>
          <button type="button" className="jc-link" onClick={onClose}>Close</button>
        </div>
      );
    }

    if (view === "error") {
      return (
        <div className="jc-center">
          <p className="jc-title">Couldn't join Circle</p>
          <p className="jc-sub" role="alert">{note}</p>
          <button type="button" className="jc-btn" disabled={busy} onClick={() => run(lastInput.current)}>Try again</button>
          <button type="button" className="jc-link" onClick={onClose}>Close</button>
        </div>
      );
    }

    if (view === "otp") {
      return (
        <div className="jc-form">
          <p className="jc-title">Verify your email</p>
          <p className="jc-sub">Enter the 6-digit code we sent to <strong>{emailMasked}</strong>.</p>
          <input
            className="jc-input jc-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={6}
            placeholder="000000"
            aria-label="6-digit code"
            value={code}
            autoFocus
            disabled={busy}
            onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); if (note) setNote(""); }}
          />
          {note && <p className="jc-error" role="alert">{note}</p>}
          <div className="jc-row">
            <button type="button" className="jc-link" disabled={busy || resendIn > 0} onClick={() => run({ name: name || undefined, email })}>
              {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
            </button>
            <button type="button" className="jc-link" disabled={busy} onClick={() => { setMissing(["email"]); setEmail(""); setNote(""); setView("details"); }}>
              Use a different email
            </button>
          </div>
          {busy && <p className="jc-sub jc-busy" role="status">Checking…</p>}
        </div>
      );
    }

    // details
    return (
      <form className="jc-form" onSubmit={submitDetails} noValidate>
        <p className="jc-title">Almost there</p>
        <p className="jc-sub">{note || "Add your details to join Circle."}</p>
        {missing.includes("name") && (
          <label className="jc-field">
            <span>Full name</span>
            <input
              className="jc-input"
              type="text"
              autoComplete="name"
              autoCapitalize="words"
              maxLength={60}
              placeholder="Your name"
              value={name}
              autoFocus
              onChange={(e) => { setName(e.target.value); setFieldError(""); }}
            />
          </label>
        )}
        {missing.includes("email") && (
          <label className="jc-field">
            <span>Email</span>
            <input
              className="jc-input"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              maxLength={254}
              placeholder="name@example.com"
              value={email}
              autoFocus={!missing.includes("name")}
              onChange={(e) => { setEmail(e.target.value); setFieldError(""); }}
            />
          </label>
        )}
        <label className="jc-terms">
          <input type="checkbox" checked={terms} onChange={(e) => { setTerms(e.target.checked); setFieldError(""); }} />
          <span>I agree to the Circle <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a>.</span>
        </label>
        {fieldError && <p className="jc-error" role="alert">{fieldError}</p>}
        <button type="submit" className="jc-btn" disabled={busy}>{busy ? "Please wait…" : "Send codes"}</button>
      </form>
    );
  };

  return createPortal(
    <div className="jc-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={sheetRef} className="jc-sheet" role="dialog" aria-modal="true" aria-label="Join Circle">
        <div className="jc-head">
          <span className="jc-head-title"><CoinIcon size={22} /> Circle</span>
          <button type="button" className="jc-close" aria-label="Close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>
          </button>
        </div>
        {body()}
      </div>
    </div>,
    document.body
  );
}
